import { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { chartChromeColors } from '../../lib/chart-colors';
import type { ChartSlice } from '../../lib/overview-stats';
import { formatFaNumber } from '../../lib/utils';
import { IndexerIcon } from '../IndexerIcon';

echarts.use([PieChart, TooltipComponent, CanvasRenderer]);

export function DonutChart({
  slices,
  centerLabel,
  centerValue,
  height = 280,
  onSliceClick,
}: {
  slices: ChartSlice[];
  centerLabel?: string;
  centerValue?: string;
  height?: number;
  onSliceClick?: (slice: ChartSlice) => void;
}) {
  const option = useMemo(() => {
    const chrome = chartChromeColors();
    return {
      textStyle: { fontFamily: 'dana' },
      tooltip: {
        trigger: 'item' as const,
        padding: [7, 10],
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        textStyle: { color: chrome.tooltipText },
        formatter: (params: {
          name?: string;
          value?: number;
          percent?: number;
          data?: ChartSlice;
        }) => {
          const pct =
            params.data?.pct ??
            (typeof params.percent === 'number' ? params.percent : 0);
          return `${params.name}<br/>${formatFaNumber(Number(params.value ?? 0))} · ${formatFaNumber(pct)}٪`;
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['58%', '82%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          padAngle: slices.length > 1 ? 2 : 0,
          itemStyle: {
            borderColor: chrome.sliceBorder,
            borderWidth: 2,
          },
          label: { show: false },
          labelLine: { show: false },
          data: slices.map((slice) => ({
            ...slice,
            name: slice.label,
            value: slice.value,
            itemStyle: { color: slice.color },
          })),
        },
      ],
    };
  }, [slices]);

  if (slices.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary border border-dashed rounded-3 bg-body-tertiary" style={{ minHeight: height }}>
        <IndexerIcon name="pie-chart" />
        <span>داده‌ای برای نمایش نیست</span>
      </div>
    );
  }

  return (
    <div className="donut-chart-wrap" style={{ height }}>
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        style={{ height: '100%', width: '100%' }}
        opts={{ renderer: 'canvas' }}
        onEvents={
          onSliceClick
            ? {
                click: (params: { data?: ChartSlice }) => {
                  if (params.data?.id) onSliceClick(params.data);
                },
              }
            : undefined
        }
      />
      {(centerLabel || centerValue) && (
        <div className="donut-chart-center" aria-hidden>
          {centerValue && (
            <div className="donut-chart-center-value">{centerValue}</div>
          )}
          {centerLabel && (
            <div className="donut-chart-center-label">{centerLabel}</div>
          )}
        </div>
      )}
    </div>
  );
}
